/**
 * Centralized error message utilities for the application
 *
 * This module provides:
 * - Standardized error messages for all application domains
 * - Error detection and classification utilities
 * - User-friendly error message formatting
 * - Integration with i18n for internationalization
 *
 * ## Usage with i18n
 *
 * For i18n-enabled components, use the translation keys directly:
 * ```typescript
 * import { useTranslation } from 'react-i18next';
 *
 * const { t } = useTranslation();
 * toast.error(t('error.auth_session_expired'));
 * ```
 *
 * For non-i18n contexts, use the constants directly:
 * ```typescript
 * import { ERROR_MESSAGES } from '@/lib/dify/errorUtils';
 *
 * toast.error(ERROR_MESSAGES.AUTH.SESSION_EXPIRED);
 * ```
 */

// ============================================================================
// DIFY API ERRORS
// ============================================================================

/**
 * Status codes that indicate Dify-related errors
 */
export const DIFY_ERROR_STATUS_CODES = [500, 502, 503, 504, 408, 429];

/**
 * Checks if a response is an HTML error page
 */
export function isHtmlErrorResponse(text: string): boolean {
  if (!text || typeof text !== 'string') return false;

  const trimmed = text.trim().toLowerCase();
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.includes('<head>') ||
    trimmed.includes('<body>')
  );
}

/**
 * Formats a Dify error into a user-friendly message
 * @param statusCode - HTTP status code
 * @param errorText - Raw error text (may contain HTML)
 * @returns Clean, user-friendly error message
 */
export function formatDifyErrorMessage(statusCode: number, errorText: string): string {
  // Check if the error is HTML and strip it
  const isHtml = isHtmlErrorResponse(errorText);

  // Generate user-friendly message based on status code
  let message = '';

  switch (statusCode) {
    case 502:
      message = 'Lema Dify error (502). Please retry sending the message';
      break;
    case 503:
      message = 'Lema Dify error (503). Service temporarily unavailable. Please retry';
      break;
    case 504:
      message = 'Lema Dify error (504). Request timeout. Please retry';
      break;
    case 500:
      message = 'Lema Dify error (500). Internal server error. Please retry';
      break;
    case 408:
      message = 'Lema Dify error (408). Request timeout. Please retry';
      break;
    case 429:
      message = 'Lema Dify error (429). Too many requests. Please wait and retry';
      break;
    default:
      // For other error codes, show generic message with code
      if (DIFY_ERROR_STATUS_CODES.includes(statusCode)) {
        message = `Lema Dify error (${statusCode}). Please retry sending the message`;
      } else {
        // If it's not a known Dify error, preserve some of the original message
        if (isHtml) {
          message = `Dify API error (${statusCode}). Please retry`;
        } else {
          // Truncate non-HTML errors to keep them readable
          const truncated = errorText.length > 200 ? `${errorText.slice(0, 200)}...` : errorText;
          message = `Dify API error (${statusCode}): ${truncated}`;
        }
      }
  }

  return message;
}

/**
 * Checks if an error message indicates a Dify error
 */
export function isDifyError(error: any): boolean {
  if (!error) return false;

  const message = error.message || String(error);
  return message.includes('Dify API error') || message.includes('Lema Dify error');
}

/**
 * Extracts the status code from a Dify error message
 */
export function extractDifyErrorCode(errorMessage: string): number | null {
  if (!errorMessage) return null;

  // Match patterns like "Dify API error: 502" or "Lema Dify error (502)"
  const match = errorMessage.match(/(?:Dify API error:\s*|Lema Dify error\s*\()(\d{3})\)?/i);
  return match ? parseInt(match[1], 10) : null;
}

// ============================================================================
// CENTRALIZED ERROR MESSAGES
// ============================================================================

/**
 * Stable error codes for runtime comparison
 * Use these for logic that compares error types (e.g., checking if error is a recovery failure)
 * Maps to the corresponding ERROR_I18N_KEYS for translation
 */
export const ERROR_CODES = {
  AUTH: {
    VERIFICATION_FAILED: 'error.auth_verification_failed',
    GOOGLE_SIGNIN_FAILED: 'error.auth_google_signin_failed',
    GOOGLE_SIGNUP_FAILED: 'error.auth_google_signup_failed',
    PASSWORD_MISMATCH: 'error.auth_password_mismatch',
    SESSION_EXPIRED: 'error.auth_session_expired',
    SIGN_OUT_FAILED: 'error.auth_sign_out_failed',
    UPDATE_STATE_FAILED: 'error.auth_update_state_failed',
    REFRESH_SESSION_FAILED: 'error.auth_refresh_session_failed',
    INIT_AUTH_FAILED: 'error.auth_init_auth_failed',
  },
  CHAT: {
    DIFY_AGENT_ERROR: 'error.dify_agent_message',
    DIFY_AGENT_ERROR_TITLE: 'error.dify_agent_title',
    CONTENT_POLICY_ERROR_TITLE: 'error.content_policy_title',
    CONTENT_POLICY_ERROR_MESSAGE: 'error.content_policy_message',
    RATE_LIMIT_EXCEEDED: 'error.chat_rate_limit_exceeded',
    CRITICAL_ERROR: 'error.chat_critical_error',
    REGENERATE_FAILED: 'error.chat_regenerate_failed',
    RESEND_FAILED: 'error.chat_resend_failed',
    STREAM_ERROR: 'error.chat_stream_error',
    STREAM_ERROR_TITLE: 'error.chat_stream_error_title',
    STREAM_ERROR_MESSAGE: 'error.chat_stream_error_message',
    READ_STREAM_ERROR: 'error.chat_read_stream_error',
    TIMEOUT: 'error.chat_timeout',
    NO_RESPONSE_BODY: 'error.chat_no_response_body',
    STOP_STREAMING_FAILED: 'error.chat_stop_streaming_failed',
    INIT_CHATBOT_FAILED: 'error.chat_init_chatbot_failed',
    FETCH_HISTORY_FAILED: 'error.chat_fetch_history_failed',
    CREATE_CHAT_FAILED: 'error.chat_create_chat_failed',
    CREATE_CONVERSATION_FAILED: 'error.chat_create_conversation_failed',
    SEND_MESSAGE_FAILED: 'error.chat_send_message_failed',
    CONVERSATION_ERROR: 'error.chat_conversation_error',
    NEW_CONVERSATION_ERROR: 'error.chat_new_conversation_error',
    PROCESSING_ERROR: 'error.chat_processing_error',
    GENERIC_ERROR: 'error.chat_generic_error',
    RECOVER_FAILED: 'error.chat_recover_failed',
    COULD_NOT_RECOVER_RESPONSE: 'error.could_not_recover_response',
  },
  FILE: {
    UPLOAD_FAILED: 'error.file_upload_failed',
    FILE_TOO_LARGE: 'error.file_too_large',
    COMPRESS_FIRST: 'error.file_compress_first',
    INVALID_FORMAT: 'error.file_invalid_format',
    CAPTION_FAILED: 'error.file_caption_failed',
    CAPTION_TIMEOUT: 'error.file_caption_timeout',
    CAPTION_INVALID_RESPONSE: 'error.file_caption_invalid_response',
    URL_UPLOAD_FAILED: 'error.file_url_upload_failed',
  },
  RATE_LIMIT: {
    TITLE: 'error.rate_limit_title',
    MESSAGE: 'error.rate_limit_message',
    MONTHLY_EXCEEDED: 'error.rate_limit_monthly_exceeded',
    WAIT_AND_RETRY: 'error.rate_limit_wait_and_retry',
  },
  SOCIAL: {
    LOAD_INSTAGRAM_FAILED: 'error.social_load_instagram_failed',
    LOAD_TIKTOK_FAILED: 'error.social_load_tiktok_failed',
    LOAD_YOUTUBE_FAILED: 'error.social_load_youtube_failed',
    DISCONNECT_FAILED: 'error.social_disconnect_failed',
    CONNECTION_FAILED: 'error.social_connection_failed',
    TOKEN_EXCHANGE_FAILED: 'error.social_token_exchange_failed',
    SAVE_CONNECTION_FAILED: 'error.social_save_connection_failed',
    FETCH_ACCOUNTS_FAILED: 'error.social_fetch_accounts_failed',
    SELECT_ACCOUNT_FAILED: 'error.social_select_account_failed',
    INDEXING_FAILED: 'error.social_indexing_failed',
    INDEXING_TIMEOUT: 'error.social_indexing_timeout',
    SOCIAL_FETCH_TIMEOUT: 'error.social_fetch_timeout',
  },
  PORTFOLIO: {
    ENTER_USERNAME: 'error.portfolio_enter_username',
    USER_NOT_FOUND: 'error.portfolio_user_not_found',
    CONNECT_FIRST: 'error.portfolio_connect_first',
    GENERATION_FAILED: 'error.portfolio_generation_failed',
    GENERIC_ERROR: 'error.portfolio_generic_error',
    NOT_FOUND_TITLE: 'error.portfolio_not_found_title',
  },
  SEARCH: {
    UNEXPECTED_RESPONSE: 'error.search_unexpected_response',
    AUTH_REQUIRED: 'error.search_auth_required',
    CONNECT_ACCOUNT_FIRST: 'error.search_connect_account_first',
    SEARCH_FAILED: 'error.search_failed',
    UNEXPECTED_ERROR: 'error.search_unexpected_error',
  },
  VERIFICATION: {
    RATE_LIMIT: 'error.verification_rate_limit',
    SEND_FAILED: 'error.verification_send_failed',
  },
  GENERAL: {
    UNEXPECTED_ERROR: 'error.general_unexpected_error',
    NETWORK_ERROR: 'error.general_network_error',
    TIMEOUT: 'error.general_timeout',
    UNKNOWN_ERROR: 'error.general_unknown_error',
    FETCH_FAILED: 'error.general_fetch_failed',
    SAVE_FAILED: 'error.general_save_failed',
    LOAD_FAILED: 'error.general_load_failed',
  },
} as const;

/**
 * i18n translation keys for error messages
 * Use these with useTranslation() hook: t('error.auth_session_expired')
 */
export const ERROR_I18N_KEYS = {
  AUTH: {
    VERIFICATION_FAILED: 'error.auth_verification_failed',
    GOOGLE_SIGNIN_FAILED: 'error.auth_google_signin_failed',
    GOOGLE_SIGNUP_FAILED: 'error.auth_google_signup_failed',
    PASSWORD_MISMATCH: 'error.auth_password_mismatch',
    SESSION_EXPIRED: 'error.auth_session_expired',
    SIGN_OUT_FAILED: 'error.auth_sign_out_failed',
    UPDATE_STATE_FAILED: 'error.auth_update_state_failed',
    REFRESH_SESSION_FAILED: 'error.auth_refresh_session_failed',
    INIT_AUTH_FAILED: 'error.auth_init_auth_failed',
  },
  CHAT: {
    DIFY_AGENT_ERROR: 'error.dify_agent_message',
    DIFY_AGENT_ERROR_TITLE: 'error.dify_agent_title',
    CONTENT_POLICY_ERROR_TITLE: 'error.content_policy_title',
    CONTENT_POLICY_ERROR_MESSAGE: 'error.content_policy_message',
    RATE_LIMIT_EXCEEDED: 'error.chat_rate_limit_exceeded',
    CRITICAL_ERROR: 'error.chat_critical_error',
    REGENERATE_FAILED: 'error.chat_regenerate_failed',
    RESEND_FAILED: 'error.chat_resend_failed',
    STREAM_ERROR: 'error.chat_stream_error',
    STREAM_ERROR_TITLE: 'error.chat_stream_error_title',
    STREAM_ERROR_MESSAGE: 'error.chat_stream_error_message',
    READ_STREAM_ERROR: 'error.chat_read_stream_error',
    TIMEOUT: 'error.chat_timeout',
    NO_RESPONSE_BODY: 'error.chat_no_response_body',
    STOP_STREAMING_FAILED: 'error.chat_stop_streaming_failed',
    INIT_CHATBOT_FAILED: 'error.chat_init_chatbot_failed',
    FETCH_HISTORY_FAILED: 'error.chat_fetch_history_failed',
    CREATE_CHAT_FAILED: 'error.chat_create_chat_failed',
    CREATE_CONVERSATION_FAILED: 'error.chat_create_conversation_failed',
    SEND_MESSAGE_FAILED: 'error.chat_send_message_failed',
    CONVERSATION_ERROR: 'error.chat_conversation_error',
    NEW_CONVERSATION_ERROR: 'error.chat_new_conversation_error',
    PROCESSING_ERROR: 'error.chat_processing_error',
    GENERIC_ERROR: 'error.chat_generic_error',
    RECOVER_FAILED: 'error.chat_recover_failed',
    COULD_NOT_RECOVER_RESPONSE: 'error.could_not_recover_response',
  },
  FILE: {
    UPLOAD_FAILED: 'error.file_upload_failed',
    FILE_TOO_LARGE: 'error.file_too_large',
    COMPRESS_FIRST: 'error.file_compress_first',
    INVALID_FORMAT: 'error.file_invalid_format',
    CAPTION_FAILED: 'error.file_caption_failed',
    CAPTION_TIMEOUT: 'error.file_caption_timeout',
    CAPTION_INVALID_RESPONSE: 'error.file_caption_invalid_response',
    URL_UPLOAD_FAILED: 'error.file_url_upload_failed',
  },
  RATE_LIMIT: {
    TITLE: 'error.rate_limit_title',
    MESSAGE: 'error.rate_limit_message',
    MONTHLY_EXCEEDED: 'error.rate_limit_monthly_exceeded',
    WAIT_AND_RETRY: 'error.rate_limit_wait_and_retry',
  },
  SOCIAL: {
    LOAD_INSTAGRAM_FAILED: 'error.social_load_instagram_failed',
    LOAD_TIKTOK_FAILED: 'error.social_load_tiktok_failed',
    LOAD_YOUTUBE_FAILED: 'error.social_load_youtube_failed',
    DISCONNECT_FAILED: 'error.social_disconnect_failed',
    CONNECTION_FAILED: 'error.social_connection_failed',
    TOKEN_EXCHANGE_FAILED: 'error.social_token_exchange_failed',
    SAVE_CONNECTION_FAILED: 'error.social_save_connection_failed',
    FETCH_ACCOUNTS_FAILED: 'error.social_fetch_accounts_failed',
    SELECT_ACCOUNT_FAILED: 'error.social_select_account_failed',
    INDEXING_FAILED: 'error.social_indexing_failed',
    INDEXING_TIMEOUT: 'error.social_indexing_timeout',
    SOCIAL_FETCH_TIMEOUT: 'error.social_fetch_timeout',
  },
  PORTFOLIO: {
    ENTER_USERNAME: 'error.portfolio_enter_username',
    USER_NOT_FOUND: 'error.portfolio_user_not_found',
    CONNECT_FIRST: 'error.portfolio_connect_first',
    GENERATION_FAILED: 'error.portfolio_generation_failed',
    GENERIC_ERROR: 'error.portfolio_generic_error',
    NOT_FOUND_TITLE: 'error.portfolio_not_found_title',
  },
  SEARCH: {
    UNEXPECTED_RESPONSE: 'error.search_unexpected_response',
    AUTH_REQUIRED: 'error.search_auth_required',
    CONNECT_ACCOUNT_FIRST: 'error.search_connect_account_first',
    SEARCH_FAILED: 'error.search_failed',
    UNEXPECTED_ERROR: 'error.search_unexpected_error',
  },
  VERIFICATION: {
    RATE_LIMIT: 'error.verification_rate_limit',
    SEND_FAILED: 'error.verification_send_failed',
  },
  GENERAL: {
    UNEXPECTED_ERROR: 'error.general_unexpected_error',
    NETWORK_ERROR: 'error.general_network_error',
    TIMEOUT: 'error.general_timeout',
    UNKNOWN_ERROR: 'error.general_unknown_error',
    FETCH_FAILED: 'error.general_fetch_failed',
    SAVE_FAILED: 'error.general_save_failed',
    LOAD_FAILED: 'error.general_load_failed',
  },
} as const;

/**
 * Success message i18n keys
 */
export const SUCCESS_I18N_KEYS = {
  AUTH: {
    SIGN_IN_SUCCESS: 'success.auth_sign_in_success',
    SIGN_OUT_SUCCESS: 'success.auth_sign_out_success',
    VERIFICATION_SENT: 'success.auth_verification_sent',
  },
  SOCIAL: {
    CONNECTED: 'success.social_connected',
    DISCONNECTED: 'success.social_disconnected',
    INDEXING_COMPLETE: 'success.social_indexing_complete',
  },
  PORTFOLIO: {
    GENERATED: 'success.portfolio_generated',
  },
  FILE: {
    UPLOADED: 'success.file_uploaded',
  },
  CHAT: {
    MESSAGE_SENT: 'success.chat_message_sent',
  },
} as const;

/**
 * Warning message i18n keys
 */
export const WARNING_I18N_KEYS = {
  FILE: {
    SIZE_WARNING: 'warning.file_size_warning',
    FORMAT_WARNING: 'warning.file_format_warning',
  },
  SOCIAL: {
    DISCONNECTION_WARNING: 'warning.social_disconnection_warning',
    INDEXING_IN_PROGRESS: 'warning.social_indexing_in_progress',
  },
  CHAT: {
    LONG_RESPONSE: 'warning.chat_long_response',
    INCOMPLETE_RESPONSE_TITLE: 'warning.chat_incomplete_response_title',
    INCOMPLETE_RESPONSE: 'warning.chat_incomplete_response',
  },
} as const;

/**
 * Centralized error messages organized by domain (English only)
 * These provide standardized error messages across the application
 * For i18n support, use ERROR_I18N_KEYS with useTranslation() instead
 */
export const ERROR_MESSAGES = {
  // Authentication Errors
  AUTH: {
    VERIFICATION_FAILED: 'Verification failed. Please try again.',
    GOOGLE_SIGNIN_FAILED: 'Failed to initiate Google sign-in. Please try again.',
    GOOGLE_SIGNUP_FAILED: 'Failed to initiate Google sign-up. Please try again.',
    PASSWORD_MISMATCH: 'Passwords do not match',
    SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
    SIGN_OUT_FAILED: 'Failed to sign out',
    UPDATE_STATE_FAILED: 'Failed to update authentication state',
    REFRESH_SESSION_FAILED: 'Failed to refresh session',
    INIT_AUTH_FAILED: 'Failed to initialize authentication',
  },

  // Chat and AI Errors
  CHAT: {
    DIFY_AGENT_ERROR: 'Lema encountered an error. Regenerate or send a different message',
    RATE_LIMIT_EXCEEDED: 'Rate limit exceeded. Please upgrade your plan.',
    CRITICAL_ERROR: 'A critical error occurred.',
    REGENERATE_FAILED: 'Error regenerating response.',
    RESEND_FAILED: 'Error resending message.',
    STREAM_ERROR: 'Stream Error',
    STREAM_ERROR_TITLE: 'Connection Lost',
    STREAM_ERROR_MESSAGE: 'The connection was interrupted. This might be a temporary network issue. Please try again.',
    READ_STREAM_ERROR: 'Error reading response stream.',
    TIMEOUT: 'Request timed out. Please try again.',
    NO_RESPONSE_BODY: 'No response body received',
    STOP_STREAMING_FAILED: 'Failed to stop streaming',
    INIT_CHATBOT_FAILED: 'Error initializing chatbot',
    FETCH_HISTORY_FAILED: 'Failed to fetch message history',
    CREATE_CHAT_FAILED: 'Error creating new chat',
    CREATE_CONVERSATION_FAILED: 'Error creating new conversation',
    SEND_MESSAGE_FAILED: 'Error sending message',
    CONVERSATION_ERROR: 'Sorry, there was an error with the conversation. Please try again.',
    NEW_CONVERSATION_ERROR: 'Sorry, there was an error creating a new conversation. Please try again.',
    PROCESSING_ERROR: 'Sorry, there was an error processing your request. Please try again.',
    GENERIC_ERROR: 'Sorry, there was an error processing your request.',
    RECOVER_FAILED: 'Could not recover response from server. Please create new chat and send a new message',
  },

  // File Operation Errors
  FILE: {
    UPLOAD_FAILED: 'Error uploading file',
    FILE_TOO_LARGE: 'File size exceeds the maximum limit',
    COMPRESS_FIRST: 'Please compress files first or try smaller files.',
    INVALID_FORMAT: 'Invalid file format',
    CAPTION_FAILED: 'Captioning failed',
    CAPTION_TIMEOUT: 'Captioning request timed out.',
    CAPTION_INVALID_RESPONSE: 'Captioning request succeeded but response was not valid JSON.',
    URL_UPLOAD_FAILED: 'URL upload failed',
  },

  // Rate Limiting Errors
  RATE_LIMIT: {
    TITLE: 'Rate Limit Reached',
    MESSAGE: 'Try again in 1 minute or upgrade to paid plan to experience less disruption',
    MONTHLY_EXCEEDED: 'Your monthly message limit has been exceeded. Please upgrade your tier to continue.',
    WAIT_AND_RETRY: 'Too many requests. Please wait and retry',
  },

  // Social Media Connection Errors
  SOCIAL: {
    LOAD_INSTAGRAM_FAILED: 'Failed to load Instagram data',
    LOAD_TIKTOK_FAILED: 'Failed to load TikTok data',
    LOAD_YOUTUBE_FAILED: 'Failed to load YouTube data',
    DISCONNECT_FAILED: 'Failed to disconnect account',
    CONNECTION_FAILED: 'Connection failed',
    TOKEN_EXCHANGE_FAILED: 'Token exchange failed',
    SAVE_CONNECTION_FAILED: 'An error occurred while saving the connection.',
    FETCH_ACCOUNTS_FAILED: 'Failed to fetch accounts',
    SELECT_ACCOUNT_FAILED: 'Failed to select account',
    INDEXING_FAILED: 'Indexing failed',
    INDEXING_TIMEOUT: 'Indexing timed out while waiting for completion',
    SOCIAL_FETCH_TIMEOUT: 'Social fetch timed out while waiting for completion',
  },

  // Portfolio Errors
  PORTFOLIO: {
    ENTER_USERNAME: 'Please enter a username',
    USER_NOT_FOUND: 'Username not found in our system.',
    CONNECT_FIRST: 'Please connect the account first in',
    GENERATION_FAILED: 'Failed to generate portfolio',
    GENERIC_ERROR: 'An error occurred while generating the portfolio',
    NOT_FOUND_TITLE: 'Portfolio Not Found',
  },

  // Brand Search Errors
  SEARCH: {
    UNEXPECTED_RESPONSE: 'Received unexpected response format from server',
    AUTH_REQUIRED: 'Authentication required. Please log in again.',
    CONNECT_ACCOUNT_FIRST: 'Please connect a social media account before searching. Go to your account settings to connect an account.',
    SEARCH_FAILED: 'Failed to perform search',
    UNEXPECTED_ERROR: 'An unexpected error occurred',
  },

  // Email Verification Errors
  VERIFICATION: {
    RATE_LIMIT: 'Please wait before trying again',
    SEND_FAILED: 'Failed to send verification email. Please try again.',
  },

  // General Application Errors
  GENERAL: {
    UNEXPECTED_ERROR: 'We apologize for the inconvenience. An unexpected error has occurred.',
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    UNKNOWN_ERROR: 'Unknown error occurred',
    FETCH_FAILED: 'Failed to fetch data',
    SAVE_FAILED: 'Failed to save changes',
    LOAD_FAILED: 'Unable to load data right now.',
  },
} as const;

/**
 * Success messages for positive user feedback
 */
export const SUCCESS_MESSAGES = {
  AUTH: {
    SIGN_IN_SUCCESS: 'Successfully signed in',
    SIGN_OUT_SUCCESS: 'Successfully signed out',
    VERIFICATION_SENT: 'Verification email sent',
  },
  SOCIAL: {
    CONNECTED: 'Account connected successfully',
    DISCONNECTED: 'Account disconnected successfully',
    INDEXING_COMPLETE: 'Indexing completed successfully',
  },
  PORTFOLIO: {
    GENERATED: 'Portfolio generated successfully',
  },
  FILE: {
    UPLOADED: 'File uploaded successfully',
  },
  CHAT: {
    MESSAGE_SENT: 'Message sent successfully',
  },
} as const;

/**
 * Warning messages for user awareness
 */
export const WARNING_MESSAGES = {
  FILE: {
    SIZE_WARNING: 'Large file detected. Consider compressing before upload.',
    FORMAT_WARNING: 'This file format may not be supported.',
  },
  SOCIAL: {
    DISCONNECTION_WARNING: 'Disconnecting will remove access to your data.',
    INDEXING_IN_PROGRESS: 'Indexing is in progress. Please do not close this page.',
  },
  CHAT: {
    LONG_RESPONSE: 'Generating a detailed response. This may take a moment.',
  },
} as const;

// ============================================================================
// ERROR FORMATTING UTILITIES
// ============================================================================

/**
 * Formats an error message with optional context
 */
export function formatErrorMessage(message: string, context?: Record<string, any>): string {
  if (!context) return message;

  let formatted = message;
  for (const [key, value] of Object.entries(context)) {
    formatted = formatted.replaceAll(`{{${key}}}`, String(value));
  }
  return formatted;
}

/**
 * Creates a user-friendly error message from an error object
 */
export function getUserFriendlyError(error: any): string {
  if (!error) return ERROR_MESSAGES.GENERAL.UNKNOWN_ERROR;

  // If it's already a string, return it
  if (typeof error === 'string') return error;

  // If it has a message property
  if (error.message) return error.message;

  // If it's an object, try to stringify it
  try {
    return JSON.stringify(error);
  } catch {
    return ERROR_MESSAGES.GENERAL.UNKNOWN_ERROR;
  }
}

/**
 * Checks if an error is a network error
 */
export function isNetworkError(error: any): boolean {
  if (!error) return false;

  const message = error.message || String(error);
  const networkIndicators = [
    'network',
    'fetch failed',
    'failed to fetch',
    'connection',
    'net::err',
  ];

  return networkIndicators.some(indicator =>
    message.toLowerCase().includes(indicator)
  );
}

/**
 * Checks if an error is a timeout error
 */
export function isTimeoutError(error: any): boolean {
  if (!error) return false;

  const message = error.message || String(error);
  const timeoutIndicators = ['timeout', 'timed out', 'aborted'];

  return timeoutIndicators.some(indicator =>
    message.toLowerCase().includes(indicator)
  );
}

/**
 * Checks if an error is an authentication error
 */
export function isAuthError(error: any): boolean {
  if (!error) return false;

  // Check status code property first
  if (error.status === 401) return true;

  const message = error.message || String(error);
  const authIndicators = [
    'unauthorized',
    'authentication',
    'auth required',
    'not authenticated',
    'session expired',
  ];

  const hasIndicator = authIndicators.some(indicator =>
    message.toLowerCase().includes(indicator)
  );

  // Check for 401 as a standalone word to avoid matching '4010' or '1401'
  return hasIndicator || /\b401\b/.test(message);
}

/**
 * Checks if an error is a rate limit error
 */
export function isRateLimitError(error: any): boolean {
  if (!error) return false;

  // Check status code
  if (error.status === 429) return true;

  const message = error.message || String(error);
  const rateLimitIndicators = [
    'rate limit',
    'monthly limit',
    'limit has been exceeded',
    'error code: 429',
    'error_code: 429',
    'too many requests',
  ];

  return rateLimitIndicators.some(indicator =>
    message.toLowerCase().includes(indicator)
  );
}

// ============================================================================
// ERROR LOGGING UTILITIES
// ============================================================================

/**
 * Logs an error to console (only in development)
 */
export function logError(context: string, error: any, additionalInfo?: Record<string, any>): void {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, error, additionalInfo);
  }
}

/**
 * Logs a warning to console (only in development)
 */
export function logWarning(context: string, message: string, additionalInfo?: Record<string, any>): void {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[${context}]`, message, additionalInfo);
  }
}
